import React, { useEffect, useRef, useState } from 'react'
import type { CreateTareaDTO, KeyValue, TareaDTO, TareaPrioridad, TareaTipo, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { endOfDayFromDateInput, todayIso } from '../../../utils/dates'
import { DESCRIPCION_MAX, errorMessage, fetchSellerOptions, fetchTaskCatalog, NOMBRE_MAX, PRIORITY_DOTS, PRIORITY_OPTIONS } from '../common/task-common'
import type { SellerOption } from '../common/task-common'

export { DESCRIPCION_MAX, NOMBRE_MAX } from '../common/task-common'

export interface TaskFormDrawerProps {
    open: boolean
    onClose: () => void
    onCreated?: (tarea: TareaDTO) => void
}

export interface TaskFormDrawerState {
    nombre: string
    prioridad: TareaPrioridad
    tipo: TareaTipo
    vendedor: string
    vencimiento: string
    descripcion: string
}

const LOAD_ERROR = 'No se pudieron cargar los catálogos de la tarea.'
const SAVE_ERROR = 'Error al guardar la tarea'
const DEFAULT_TIPOS: KeyValue[] = [{ key: 'tarea', value: 'Tarea' }]

const emptyState = (): TaskFormDrawerState => ({
    nombre: '',
    prioridad: 'media',
    tipo: 'tarea',
    vendedor: '',
    vencimiento: todayIso(),
    descripcion: '',
})

const toRequest = (state: TaskFormDrawerState): CreateTareaDTO => ({
    nombre: state.nombre.trim(),
    descripcion: state.descripcion.trim(),
    vencimiento: endOfDayFromDateInput(state.vencimiento),
    prioridad: state.prioridad,
    tipo: state.tipo,
    vendedor: state.vendedor ? Number(state.vendedor) : null,
    status: 'abierta',
})

export const useTaskFormDrawerModel = ({ open, onClose, onCreated }: TaskFormDrawerProps) => {
    const catalogsLoaded = useRef(false)
    const [state, setState] = useState<TaskFormDrawerState>(emptyState)
    const [tipos, setTipos] = useState<KeyValue[]>([])
    const [sellers, setSellers] = useState<SellerOption[]>([])
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!open) return
        setState(emptyState())
        setError(null)
    }, [open])

    useEffect(() => {
        if (!open || catalogsLoaded.current) return undefined
        let active = true
        const load = async () => {
            try {
                const [tipoList, sellerList] = await Promise.all([fetchTaskCatalog('/tareas/tipos'), fetchSellerOptions()])
                if (!active) return
                catalogsLoaded.current = true
                setTipos(tipoList)
                setSellers(sellerList)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            }
        }
        load()
        return () => { active = false }
    }, [open])

    const patch = (partial: Partial<TaskFormDrawerState>) => setState((current) => ({ ...current, ...partial }))

    const change = {
        nombre: (value: string) => patch({ nombre: value.slice(0, NOMBRE_MAX) }),
        prioridad: (value: TareaPrioridad) => patch({ prioridad: value }),
        tipo: (value: string) => patch({ tipo: value as TareaTipo }),
        vendedor: (value: string) => patch({ vendedor: value }),
        vencimiento: (value: string) => patch({ vencimiento: value }),
        descripcion: (value: string) => patch({ descripcion: value.slice(0, DESCRIPCION_MAX) }),
    }

    const canSave = Boolean(state.nombre.trim() && state.descripcion.trim() && state.prioridad && state.tipo)

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!canSave || saving) return
        try {
            setSaving(true)
            setError(null)
            const payload: WrapperResponse<TareaDTO> = await (await APIInvoke.invokePOST('/tareas', toRequest(state))).json()
            if (!payload.ok || !payload.body) throw new Error(payload.message || SAVE_ERROR)
            onCreated?.(payload.body)
            onClose()
        } catch (saveError) {
            setError(errorMessage(saveError, SAVE_ERROR))
        } finally {
            setSaving(false)
        }
    }

    return {
        form: state,
        priorities: PRIORITY_OPTIONS.map((option) => ({ ...option, dotClass: PRIORITY_DOTS[option.key], selected: option.key === state.prioridad })),
        tipos: tipos.length ? tipos : DEFAULT_TIPOS,
        sellers,
        saving,
        error,
        canSave,
        change,
        save,
        cancel: onClose,
    }
}
